import FilterIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Filter.js';
import UndoIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Undo.js';
import {
  Box,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Button } from 'src/components/atoms';
import {
  DisclosureDataset,
  DisclosureFilterColumn,
  DisclosureFilterValue,
  FilterOptionsByColumn,
} from 'src/components/pages/CoiDisclosures/types';
import {
  DisclosuresFilterPopover,
  FilterRow,
} from './DisclosuresFilterPopover';
import * as styles from './DisclosuresToolbar.styles';

export interface DisclosuresToolbarProps {
  searchTerm: string;
  onSearch: (value: string) => void;
  onReset: () => void;
  dataset: DisclosureDataset;
  onDatasetChange: (dataset: DisclosureDataset) => void;
  filterValue: DisclosureFilterValue;
  onFilterChange: (value: DisclosureFilterValue) => void;
  filterRows: FilterRow[];
  onFilterRowsChange: (rows: FilterRow[]) => void;
  onRemoveAllFilters: () => void;
  makeFilterId: () => string;
  filterOptions: FilterOptionsByColumn;
  visibleColumns: DisclosureFilterColumn[];
}

export const DisclosuresToolbar: React.FC<DisclosuresToolbarProps> = ({
  searchTerm,
  onSearch,
  onReset,
  dataset,
  onDatasetChange,
  filterValue,
  onFilterChange,
  filterRows,
  onFilterRowsChange,
  onRemoveAllFilters,
  makeFilterId,
  filterOptions,
  visibleColumns,
}) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  return (
    <Box sx={styles.container}>
      <Box sx={styles.leftGroup}>
        <Box sx={styles.searchField}>
          <Typography sx={styles.searchLabel}>Search</Typography>
          <TextField
            size="medium"
            value={searchTerm}
            placeholder="Search by all fields"
            onChange={(e) => onSearch(e.target.value)}
            slotProps={{
              htmlInput: { 'data-testid': 'disclosures-toolbar-search' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <span className="uil uil-search" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <Button
          data-testid="disclosures-toolbar-filter"
          onClick={(e: React.MouseEvent<HTMLElement>) =>
            setFilterAnchorEl(e.currentTarget)
          }
          style={styles.filterButton}
        >
          <FilterIcon />
          Filter
        </Button>
        <DisclosuresFilterPopover
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={() => setFilterAnchorEl(null)}
          rows={filterRows}
          onRowsChange={onFilterRowsChange}
          value={filterValue}
          onChange={onFilterChange}
          onRemoveAll={() => {
            onRemoveAllFilters();
            setFilterAnchorEl(null);
          }}
          makeId={makeFilterId}
          filterOptions={filterOptions}
          visibleColumns={visibleColumns}
        />
        <Button
          variant="tertiary"
          data-testid="disclosures-toolbar-reset"
          onClick={onReset}
          style={styles.resetButton}
        >
          <UndoIcon />
          Reset
        </Button>
      </Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={dataset}
        onChange={(_, next: DisclosureDataset | null) => {
          if (next && next !== dataset) onDatasetChange(next);
        }}
        sx={styles.toggleGroup}
      >
        <ToggleButton
          value="open"
          data-testid="disclosures-toolbar-toggle-open"
        >
          Open
        </ToggleButton>
        <ToggleButton
          value="closed"
          data-testid="disclosures-toolbar-toggle-closed"
        >
          Closed
        </ToggleButton>
        <ToggleButton
          value="auto-closed"
          data-testid="disclosures-toolbar-toggle-auto-closed"
        >
          Auto-closed
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};
